import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { callAPI } from '../hooks/api'
import CustomDropdown from './CustomDropdown'
import useAbility from '@/hooks/use-ability'
import { cn } from '@/lib/utils'
import { LockToolTipIcon } from '@/components/upgrade-lock-icon-layout'

const QueryFilterModal = ({
  isOpen,
  onClose,
  setFilterGeoJson,
  setMapBounds,
  setVisibility,
  isTutorial = false,
}) => {
  const [counties, setCounties] = useState([])
  const [owners, setOwners] = useState([])
  const [gsas, setGsas] = useState([])
  const [wds, setWds] = useState([])
  const [crops, setCrops] = useState([])

  const [county, setCounty] = useState('')
  const [limit, setLimit] = useState(10)
  const [owner, setOwner] = useState('')
  const [gsa, setGsa] = useState('')
  const [waterDistrict, setWaterDistrict] = useState('')
  const [mainCrop, setMainCrop] = useState('')
  const [maxHydraScore, setMaxHydraScore] = useState(100)
  const [minHydraScore, setMinHydraScore] = useState(0)
  const [maxSoilScore, setMaxSoilScore] = useState(100)
  const [minSoilScore, setMinSoilScore] = useState(0)
  const [maxAcreage, setMaxAcreage] = useState('')
  const [minAcreage, setMinAcreage] = useState('')

  const isDisabled = !county

  const ownerInputRef = useRef(null)
  const gsaInputRef = useRef(null)
  const wdInputRef = useRef(null)
  const cropInputRef = useRef(null)

  const [previousValues, setPreviousValues] = useState({
    owner: '',
    gsa: '',
    wd: '',
    crop: '',
  })

  const onCountyChange = (e) => {
    setOwners([])
    setGsas([])
    setWds([])
    setCrops([])
    setOwner('')
    setGsa('')
    setWaterDistrict('')
    setMainCrop('')
    setCounty(e.target.value)
  }

  const onFieldChange = (e, type) => {
    const newValue = e.target.value
    const oldValue = previousValues[type] || '' // Provide a default empty string if undefined
    const diff = Math.abs(newValue.length - oldValue.length)

    setPreviousValues((prevValues) => ({
      ...prevValues,
      [type]: newValue,
    }))

    const fieldSetters = {
      owner: setOwner,
      gsa: setGsa,
      wd: setWaterDistrict,
      main_crop: setMainCrop,
    }
    const resultSetters = {
      owner: setOwners,
      gsa: setGsas,
      wd: setWds,
      main_crop: setCrops,
    }
    const resultgetters = {
      owner: owners,
      gsa: gsas,
      wd: wds,
      main_crop: crops,
    }
    if (fieldSetters[type]) fieldSetters[type](newValue)
    if (isTutorial) return

    if (newValue.length < 4) {
      if (resultSetters[type]) resultSetters[type]([])
      return
    }

    if (resultgetters[type].includes(newValue)) return

    const fetchAutoCompleteResults = () => {
      callAPI('/filter_autocomplete', {
        county,
        query_input: newValue,
        query_type: type,
      }).then((res) => {
        if (res.status === 'fail') {
          toast.warn(res.msg)
          return
        }

        if (resultSetters[type]) resultSetters[type](res.result)
      })
    }

    if (diff > 1 || newValue.length === 4) {
      fetchAutoCompleteResults()
    } else {
      const lists = {
        owner: owners,
        gsa: gsas,
        wd: wds,
        main_crop: crops,
      }
      if (lists[type]?.length === 0) {
        fetchAutoCompleteResults()
      }
    }
  }

  const runQuery = () => {
    if (isTutorial) return
    if (
      (owner && !owners.length) ||
      (gsa && !gsas.length) ||
      (waterDistrict && !wds.length) ||
      (mainCrop && !crops.length)
    ) {
      toast.warn(
        'Your input is invalid, or our data does not contain any records that match your input.'
      )
      return
    }
    if (
      (owners.length && !owners.includes(owner)) ||
      (gsas.length && !gsas.includes(gsa)) ||
      (wds.length && !wds.includes(waterDistrict)) ||
      (crops.length && !crops.includes(mainCrop))
    ) {
      toast.warn(
        'You must select your input from the list (Owner, GSA, Water District, Main Crop).'
      )
      return
    }
    var params = {
      county: county,
      owner: owner,
      gsa: gsa,
      water_district: waterDistrict,
      main_crop: mainCrop,
      max_hs: maxHydraScore,
      min_hs: minHydraScore,
      max_ss: maxSoilScore,
      min_ss: minSoilScore,
      max_acreage: maxAcreage ? maxAcreage.toString() : '',
      min_acreage: minAcreage ? minAcreage.toString() : '',
      record_limit: limit,
      session_key: JSON.parse(localStorage.auth).sessionKey,
    }
    callAPI('/filter_criteria', params).then((res) => {
      if (res.status === 'success') {
        toast.success(
          `${res.result.features.length} parcel(s) returned from the search. (Parcels without a Hydra score are not included in the results)`
        )
        setFilterGeoJson(res.result)
        setVisibility((visibility) => ({
          ...visibility,
          ['filtered_parcels']: true,
        }))
        if (Object.keys(res.result).length && res.result.features.length) {
          const bbox = res.result.bbox
          const getBoundingBox = (coordinates) => {
            const minLat = Math.min(...coordinates.map((coord) => coord[0]))
            const maxLat = Math.max(...coordinates.map((coord) => coord[0]))
            const minLon = Math.min(...coordinates.map((coord) => coord[1]))
            const maxLon = Math.max(...coordinates.map((coord) => coord[1]))

            return [
              [minLon, minLat],
              [maxLon, maxLat],
            ]
          }

          setMapBounds(
            getBoundingBox([
              [bbox[1], bbox[0]],
              [bbox[3], bbox[2]],
            ])
          )
        }
        onClose()
      } else {
        toast.warn(res.msg)
      }
    })
  }

  const styles = {
    overlay: {
      position: 'fixed',
      top: '45px',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 15px',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '20px',
      width: '100%',
      maxWidth: '600px',
      maxHeight: 'calc(100vh - 60px)',
      borderRadius: '8px',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
      overflowY: 'auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '20px',
      cursor: 'pointer',
    },
    body: {
      marginTop: '15px',
    },
    label: {
      display: 'block',
      marginBottom: '5px',
    },
    input: {
      width: '100%',
      padding: '8px',
      marginBottom: '15px',
      borderRadius: '8px', // Rounded dropdown
    },
    radioGroup: {
      display: 'flex',
      gap: '10px',
      marginBottom: '15px',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
    },
    scoreRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '10px',
      marginBottom: '15px',
    },
    runQueryButton: {
      float: 'right',
      padding: '10px 20px',
      marginTop: '15px',
      marginBottom: '13px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
    },
    runQueryButtonDisabled: {
      backgroundColor: 'grey',
      cursor: 'not-allowed',
    },
    footerPadding: {
      display: 'flex',
      justifyContent: 'flex-end', // Aligns the button to the right
      alignItems: 'center',
    },
  }

  useEffect(() => {
    var endpoint = '/get_filter_criteria_counties'
    callAPI(endpoint, {}).then((res) => {
      if (res.status === 'fail') {
        toast.warn(res.msg)
        return
      }
      setCounties(res.county_names.sort())
    })
  }, [])

  const { checkAbility } = useAbility()

  const isEnableOwnerName = checkAbility('ownership_information')
  const isEnableHydraScore = checkAbility('hydra_score')
  const isEnableSoilScore = checkAbility('soil_score')

  const CustomNumberInput = ({
    label,
    value,
    onChange,
    min = 0,
    max = 100,
    needUpgrade = false,
  }) => {
    return (
      <div style={{ flex: 1 }}>
        <div className="flex flex-row space-x-1 w-full">
          <label
            className={cn('whitespace-nowrap', needUpgrade && 'opacity-50')}
            style={styles.label}
          >
            {label}
          </label>
          <LockToolTipIcon show={needUpgrade} />
        </div>
        <input
          disable={needUpgrade}
          style={styles.input}
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={onChange}
        />
      </div>
    )
  }

  return (
    isOpen && (
      <div style={styles.overlay}>
        <div style={styles.modalContent}>
          <div style={styles.header}>
            <h3>Query Filter Criteria</h3>
            <button style={styles.closeButton} onClick={onClose}>
              X
            </button>
          </div>
          <div style={styles.body}>
            <label style={styles.label}>County:</label>
            <select
              style={styles.input}
              value={county}
              onChange={onCountyChange}
            >
              <option value="">Select a County</option>
              {counties.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <fieldset disabled={isDisabled}>
              <label style={styles.label}>Limit Results:</label>
              <div style={styles.radioGroup}>
                {[10, 50, 100, 250, 500].map((l) => (
                  <label key={l} style={styles.radioLabel}>
                    <input
                      type="radio"
                      value={l}
                      checked={limit === l}
                      onChange={() => setLimit(l)}
                    />{' '}
                    {l}
                  </label>
                ))}
              </div>

              <CustomDropdown
                needUpgrade={!isEnableOwnerName}
                label="Owner Name"
                data={owner}
                dataList={owners}
                onFieldChange={onFieldChange}
                field="owner"
              />

              <CustomDropdown
                needUpgrade={false}
                label="GSA"
                data={gsa}
                dataList={gsas}
                onFieldChange={onFieldChange}
                field="gsa"
              />

              <CustomDropdown
                needUpgrade={false}
                label="Water District"
                data={waterDistrict}
                dataList={wds}
                onFieldChange={onFieldChange}
                field="wd"
              />

              <CustomDropdown
                label="Main Crop"
                data={mainCrop}
                dataList={crops}
                onFieldChange={onFieldChange}
                field="main_crop"
              />
              {/* <label style={styles.label}>GSA:</label>
                            <input style={styles.input} ref={gsaInputRef} placeholder='type at least 4 letters' list="gsas" value={gsa} onChange={(e) => onFieldChange(e, 'gsa')} />
                            <datalist id="gsas">
                                {gsas.map((g) => (
                                    <option key={g} value={g} />
                                ))}
                            </datalist>

                            <label style={styles.label}>Water District:</label>
                            <input style={styles.input} ref={wdInputRef} placeholder='type at least 4 letters' list="waterDistricts" value={waterDistrict} onChange={(e) => onFieldChange(e, 'wd')} />
                            <datalist id="waterDistricts">
                                {wds.map((w) => (
                                    <option key={w} value={w} />
                                ))}
                            </datalist>

                            <label style={styles.label}>Main Crop:</label>
                            <input style={styles.input} ref={cropInputRef} placeholder='type at least 4 letters' list="mainCrops" value={mainCrop} onChange={(e) => onFieldChange(e, 'main_crop')} />
                            <datalist id="mainCrops">
                                {crops.map((crop) => (
                                    <option key={crop} value={crop} />
                                ))}
                            </datalist> */}

              <div style={styles.scoreRow}>
                <CustomNumberInput
                  needUpgrade={!isEnableHydraScore}
                  label="Min HydraScore"
                  value={minHydraScore}
                  onChange={(e) => setMinHydraScore(e.target.value)}
                  min={0}
                  max={100}
                />
                <CustomNumberInput
                  needUpgrade={!isEnableHydraScore}
                  label="Max HydraScore"
                  value={maxHydraScore}
                  onChange={(e) => setMaxHydraScore(e.target.value)}
                  min={0}
                  max={100}
                />
              </div>

              <div style={styles.scoreRow}>
                <CustomNumberInput
                  needUpgrade={!isEnableSoilScore}
                  label="Min SoilScore"
                  value={minSoilScore}
                  onChange={(e) => setMinSoilScore(e.target.value)}
                  min={0}
                  max={100}
                />
                <CustomNumberInput
                  needUpgrade={!isEnableSoilScore}
                  label="Max SoilScore"
                  value={maxSoilScore}
                  onChange={(e) => setMaxSoilScore(e.target.value)}
                  min={0}
                  max={100}
                />
              </div>

              <div style={styles.scoreRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Min Acreage:</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="0"
                    value={minAcreage}
                    onChange={(e) => setMinAcreage(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Max Acreage:</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="0"
                    value={maxAcreage}
                    onChange={(e) => setMaxAcreage(e.target.value)}
                  />
                </div>
              </div>
            </fieldset>
          </div>
          <div style={styles.footerPadding}>
            <button
              style={
                isDisabled
                  ? {
                      ...styles.runQueryButton,
                      ...styles.runQueryButtonDisabled,
                    }
                  : styles.runQueryButton
              }
              disabled={isDisabled}
              onClick={runQuery}
            >
              Run Query
            </button>
          </div>
        </div>
      </div>
    )
  )
}

export default QueryFilterModal
